import { 
	ChangeDetectionStrategy, 
	Component, 
	CUSTOM_ELEMENTS_SCHEMA, 
	ElementRef, 
	effect, 
	forwardRef, 
	inject, 
	OnInit, 
	signal,
	viewChild, 
	ViewEncapsulation 
} from '@angular/core';

import { injectTranslation } from '@skylab-angular-shared/core';
import { isEqual, isUndefined } from '@skylab-shared/utils';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { computed } from '@angular/core';
import { DialogInsService } from '../../../dialog/dialog-ins.service';
import { DialogState } from '../../../dialog/dialog.model';
import { SelectValueAccessorDirective } from '../../../value-accessors/select-value-accessor.directive';
import { OneFromListChip } from '../../filter-chips';
import { 
	FILTER_CHIP_STRATEGY, 
	FilterChipCollectionItem, 
	FilterChipContext, 
	FilterChipStrategy 
} from '../filter-chip-context';
import { FilterChipHostDirective } from '../filter-chip-host.directive';
import { IconComponent } from '../../../icon/icon.component';

const FilterCustomItemValue = 'Filter_Custom_Value' as const;

@Component({
	selector: 'skl-one-from-list-filter-chip',
	templateUrl: './filter-chip-one-from-list.component.html',
	hostDirectives: [{ directive: FilterChipHostDirective, inputs: ['chip'] }],
	host: {
		'[class.has-value]': 'ctx.hasValue()'
	},
	providers: [
		FilterChipContext,
		{ provide: FILTER_CHIP_STRATEGY, useExisting: forwardRef(() => SklOneFromListFilterChipComponent) }
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [SelectValueAccessorDirective, FormsModule, TranslatePipe, IconComponent],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SklOneFromListFilterChipComponent implements OnInit, FilterChipStrategy {
	public readonly select = viewChild.required<ElementRef<HTMLInsSelectElement>>('insSelectElement');

	protected readonly _T = injectTranslation();
	protected readonly ctx = inject(FilterChipContext);
	readonly #dialogService = inject(DialogInsService);
	readonly #host = inject(FilterChipHostDirective);

	#dialogData!: unknown;
	#isProcessing = false;

	public readonly chosenValues: string[] = [FilterCustomItemValue];

	protected readonly chip = computed(() => this.#host.chip() as OneFromListChip);
	protected readonly value = this.ctx.value;
	protected readonly collection = this.ctx.collection;

	protected readonly selectValue = signal<unknown>(null);

	protected get converter() { return this.ctx.converter; }

	constructor() {
		effect(() => {
			const chip = this.#host.chip();
			if (chip) this.ctx.chip.set(chip);
		});
	}

	public ngOnInit() {
		const chip = this.chip();
		this.ctx.chip.set(chip);

		const extraItems: FilterChipCollectionItem[] = [];

		if (chip.collection) {
			extraItems.push(...chip.collection);
		}
		if (chip.customValueDialog) {
			extraItems.push({
				label: chip.customValueLabel || this._T('ui.filter-chips.selected'),
				value: FilterCustomItemValue,
			});
		}

		this.ctx.buildCollection(extraItems);
		this.setInitialValue();
		this.ctx.initConverter();
	}

	public setInitialValue() {
		const chip = this.chip();
		this.value.set(chip.value);
		this.selectValue.set(this.#getSelectValue(chip.value));
		this.ctx.setInitialChipValue(chip.value);
		
		if (this.#isValueFromBaseCollection(chip.value)) {
			this.#dialogData = null;
		} else {
			this.#dialogData = chip.value;
		}
	}

	public onValueChange(value: unknown) {
		if (this.#isProcessing || (value !== FilterCustomItemValue && isEqual(value, this.value()))) {
			return;
		}

		this.selectValue.set(value);
		this.#createDialog(value);
	}

	public onOptionClick(item: FilterChipCollectionItem) {
		if (item.value === FilterCustomItemValue) {
			this.onValueChange(item.value);
		}
	}

		public isSelected(item: FilterChipCollectionItem) {
		return this.selectValue() === item.value;
	}

	public isChosenValue() {
		return this.selectValue() === FilterCustomItemValue && this.getChosenValue() !== '';
	}

	public getChosenValue() {
		const chip = this.chip();
		if (this.selectValue() !== FilterCustomItemValue || !this.#dialogData) {
			return '';
		}

		return chip.customValueDisplayText?.(this.#dialogData) ?? (this.#dialogData as string);
	}

	public isChosenItem(item: FilterChipCollectionItem) {
		return item?.value === FilterCustomItemValue;
	}

	public resetDefaultValue() {
		const chip = this.chip();
		const newValue = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
		
		if (isEqual(newValue, this.value())) {
			return;
		}

		this.value.set(newValue);
		this.selectValue.set(this.#getSelectValue(newValue));
		chip.value = newValue;
		chip.onChange(newValue);
		
		if (this.#isValueFromBaseCollection(newValue)) {
			this.#dialogData = null;
		} else {
			this.#dialogData = newValue;
		}
	}

	public refresh() {
		const chip = this.chip();
		const extraItems: FilterChipCollectionItem[] = [];
		if (chip.collection) extraItems.push(...chip.collection);
		if (chip.customValueDialog) {
			extraItems.push({
				label: chip.customValueLabel || this._T('ui.filter-chips.selected'),
				value: FilterCustomItemValue,
			});
		}
		this.ctx.buildCollection(extraItems);
		this.setInitialValue();
	}

	#isValueFromBaseCollection(value: unknown) {
		const chip = this.chip();
		const isInNormal = chip.collection?.find(item => item.value === value);
		return !isUndefined(isInNormal);
	}

	#getSelectValue(val: unknown) {
		return this.#isValueFromBaseCollection(val) ? val : ((val === null || val === undefined) ? null : FilterCustomItemValue);
	}

	async #createDialog(value: unknown): Promise<void> {
		const chip = this.chip();
		this.#isProcessing = true;

		try {
			if (value === FilterCustomItemValue) {
				const result = await this.#dialogService.showAsync({
					component: chip.customValueDialog!,
					componentProps: { 
						data: this.#dialogData
					}
				});

				if (result.state === DialogState.Confirm) {
					if (isEqual(result.data, this.value())) {
						return;
					}
					this.value.set(result.data);
					this.#dialogData = chip.value = result.data;
					chip.onChange(this.#dialogData);
				}
			} else {
				this.#dialogData = null;
				this.value.set(value);
				chip.value = value;
				chip.onChange(value);
			}
		} finally {
			this.selectValue.set(this.#getSelectValue(this.value()));
			this.#isProcessing = false;
		}
	}
}
